import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  setI18nInstance as setSharedI18nInstance,
  translateKey,
  applyLanguage,
  preloadLanguageMessages,
  resolveLanguageDisplayName,
  enabledLanguages,
  nativeNameMap,
  type TranslationParams,
} from '@shared/core/i18n';
import {
  loadLanguage,
  availableLanguages,
  isLanguageAvailable as checkLanguageAvailable,
} from 'src/i18n';
import type { TranslationKey } from 'src/i18n/types';
import { devError, devWarn } from '@shared/vue/utils/debug';
import { formatPhpDate } from 'src/utils/phpDateFormat';
import { useUserSettingsStore } from 'src/stores/userSettings';

export const I18N_STORE_ID = 'store-i18n';

/**
 * Supported languages with display metadata, derived from the shared
 * `@shared/core/i18n/languages.config.ts` single source of truth. To
 * enable / disable a language, edit that file — both UI and SI pick up
 * the change automatically.
 */
export const AVAILABLE_LANGUAGES: Record<string, { name: string; nativeName: string }> =
  Object.fromEntries(
    enabledLanguages().map((l) => [l.code, { name: l.name, nativeName: l.nativeName }]),
  );

export type ValidLanguage = string;

const NATIVE_NAMES: Record<string, string> = nativeNameMap();

// ============================================
// FORMAT TYPES & PRESETS
// ============================================

export type DateFormatPreset = 'short' | 'medium' | 'long' | 'full';
export type TimeFormatPreset = 'short' | 'medium' | 'long';
export type NumberFormatStyle = 'decimal' | 'percent' | 'currency' | 'unit';
export type RelativeTimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

export interface DateFormatOptions {
  format?: DateFormatPreset | Intl.DateTimeFormatOptions;
  timezone?: string;
  locale?: string;
}

export interface TimeFormatOptions {
  format?: TimeFormatPreset;
  timezone?: string;
  locale?: string;
  hour12?: boolean;
}

export interface NumberFormatOptions {
  style?: NumberFormatStyle;
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
  locale?: string;
  unit?: string;
  unitDisplay?: 'short' | 'long' | 'narrow';
}

const DATE_FORMAT_PRESETS: Record<DateFormatPreset, Intl.DateTimeFormatOptions> = {
  short: { year: 'numeric', month: 'numeric', day: 'numeric' },
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
};

const TIME_FORMAT_PRESETS: Record<TimeFormatPreset, Intl.DateTimeFormatOptions> = {
  short: { hour: 'numeric', minute: 'numeric' },
  medium: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
  long: { hour: 'numeric', minute: 'numeric', second: 'numeric', timeZoneName: 'short' },
};

const PHP_DATE_FORMAT_MAP: Record<string, Intl.DateTimeFormatOptions> = {
  'Y-m-d': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'd/m/Y': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'm/d/Y': { month: '2-digit', day: '2-digit', year: 'numeric' },
  'd-m-Y': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'd.m.Y': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'Y/m/d': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'M d, Y': { month: 'short', day: 'numeric', year: 'numeric' },
  'F d, Y': { month: 'long', day: 'numeric', year: 'numeric' },
  'd M Y': { day: 'numeric', month: 'short', year: 'numeric' },
  'd F Y': { day: 'numeric', month: 'long', year: 'numeric' },
};

const TIME_INTERVALS: { unit: RelativeTimeUnit; ms: number }[] = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function getDateOptionsFromPhpFormat(phpFormat: string): Intl.DateTimeFormatOptions {
  return PHP_DATE_FORMAT_MAP[phpFormat] || DATE_FORMAT_PRESETS.short;
}

/**
 * Build a BCP-47 locale tag from EMALINO's two independent concepts:
 * `language` (ISO 639-1, drives translation) and `country` (ISO 3166-1,
 * drives regional formatting). When `country` is null/undefined, the
 * tag is just the language code; `Intl.*` falls back to language-only
 * defaults, which is the right behavior for staff who haven't set a
 * country preference.
 */
function buildLocale(language: string, country?: string | null): string {
  if (country) return `${language}-${country.toUpperCase()}`;
  return language;
}

/**
 * Re-export the shared i18n instance setter from `@shared/core/i18n`
 * under the existing `setI18nInstance` name so `boot/i18n.ts` keeps
 * its current import path.
 */
export const setI18nInstance = setSharedI18nInstance;

export const useI18nStore = defineStore(I18N_STORE_ID, () => {
  const currentLanguage = ref<ValidLanguage>('en');
  const isLoading = ref(false);
  const loadError = ref<string | null>(null);
  /**
   * True once the staff member has chosen a language from a switcher. Persisted,
   * and checked by the boot-time browser detection so a deliberate choice is
   * never overwritten by `navigator.languages` on the next reload. Same
   * semantics as the UI app's flag — the two consoles must not disagree about
   * what counts as a choice.
   */
  const userPickedLanguage = ref(false);

  const language = computed(() => currentLanguage.value);

  const availableLanguagesMap = computed(() => AVAILABLE_LANGUAGES);

  const availableLanguagesList = computed(() => {
    return availableLanguages.map((code) => ({
      code,
      name: resolveLanguageDisplayName(code, NATIVE_NAMES),
    }));
  });

  const currentLanguageName = computed(
    () => AVAILABLE_LANGUAGES[currentLanguage.value]?.nativeName || 'English',
  );

  function isLanguageAvailable(lang: string): boolean {
    return Object.prototype.hasOwnProperty.call(AVAILABLE_LANGUAGES, lang) && checkLanguageAvailable(lang);
  }

  /**
   * Switch active language. Sets Pinia state synchronously so callers
   * that don't `await` still observe the change, then asynchronously
   * pushes the locale into vue-i18n (lazy-loading the bundle on first
   * use).
   */
  async function setLanguage(
    newLanguage: string,
    opts?: { userPicked?: boolean },
  ): Promise<void> {
    if (!isLanguageAvailable(newLanguage)) return;

    // Defaults to a pick: every switcher in SI calls this bare. Only the
    // boot-time auto-detect passes `{ userPicked: false }`, so it stays
    // re-runnable when the staff member's browser language changes.
    if (opts?.userPicked !== false) {
      userPickedLanguage.value = true;
    }

    // Optimistic synchronous set (so non-awaiting callers observe the change),
    // but remember the previous value so we can roll back if the bundle fails
    // to load — otherwise the store would advertise a language whose vue-i18n
    // bundle was never installed (wrong picker/persisted state, EN fallbacks).
    const previousLanguage = currentLanguage.value;
    currentLanguage.value = newLanguage;

    isLoading.value = true;
    loadError.value = null;
    try {
      await applyLanguage(newLanguage, loadLanguage);
    } catch (error) {
      currentLanguage.value = previousLanguage;
      loadError.value = `Failed to set language "${newLanguage}"`;
      devError(loadError.value, error);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Translate a key. Supports both signatures the shared engine accepts:
   *   t(key)
   *   t(key, params)
   *   t(key, count)            // pluralization
   *   t(key, count, params)    // plural + named params
   *
   * The key is typed `TranslationKey | (string & {})` (P3.4): a valid key gets
   * IDE autocomplete from the English schema while any string still type-checks,
   * so existing call sites are not broken. Tightening to `TranslationKey` alone
   * (full compile-time validation) is the follow-on that first requires closing
   * the code-vs-`en` key gaps.
   */
  function t(
    key: TranslationKey | (string & {}),
    countOrParams?: number | TranslationParams,
    params?: TranslationParams,
  ): string {
    return translateKey(key, countOrParams, params);
  }

  /** Backwards-compat: load a language without switching. */
  async function preloadLanguage(lang: string): Promise<void> {
    if (!isLanguageAvailable(lang)) return;
    await preloadLanguageMessages(lang, loadLanguage);
  }

  // ==========================================
  // FORMAT METHODS (Intl-based, user-settings aware)
  // ==========================================
  //
  // Mirror UI's `d/time/dt/n/c/rt/dateRange`. They read country /
  // currency / timezone / dateFormat / timeFormat from the staff
  // userSettings store at call time; per-staff Intl locale is built by
  // `buildLocale(language, country)`. When a staff member hasn't set a
  // country, the locale is just the language code — `Intl.*` falls back
  // to language-default formatting, which is the right behavior.

  function getUserSettings() {
    try {
      return useUserSettingsStore();
    } catch {
      return null;
    }
  }

  function d(
    value: string | number | Date | null | undefined,
    options?: DateFormatOptions,
  ): string {
    const date = parseDate(value);
    if (!date) return '';

    const settings = getUserSettings();
    const locale = options?.locale || buildLocale(language.value, settings?.country || null);
    const timezone = options?.timezone || settings?.timezone || undefined;

    // PHP-format path (the staff Settings date format): honor the chosen token
    // order + separators, which Intl.format() discards (a known Intl limitation
    // that made Y-m-d / d/m/Y / d.m.Y all render identically).
    if (!options?.format) {
      const userDateFormat = settings?.dateFormat || 'Y-m-d';
      try {
        return formatPhpDate(date, userDateFormat, locale, timezone);
      } catch (error) {
        devWarn(`Date formatting failed: ${error instanceof Error ? error.message : String(error)}`);
        return date.toLocaleDateString();
      }
    }

    const formatOptions: Intl.DateTimeFormatOptions = typeof options.format === 'string'
      ? { ...DATE_FORMAT_PRESETS[options.format] }
      : { ...options.format };
    if (timezone) formatOptions.timeZone = timezone;

    try {
      return new Intl.DateTimeFormat(locale, formatOptions).format(date);
    } catch (error) {
      devWarn(`Date formatting failed: ${error instanceof Error ? error.message : String(error)}`);
      return date.toLocaleDateString();
    }
  }

  function time(
    value: string | number | Date | null | undefined,
    options?: TimeFormatOptions,
  ): string {
    const date = parseDate(value);
    if (!date) return '';

    const settings = getUserSettings();
    const locale = options?.locale || buildLocale(language.value, settings?.country || null);
    const timezone = options?.timezone || settings?.timezone || undefined;

    const userTimeFormat = settings?.timeFormat || '24h';
    const hour12 = options?.hour12 ?? (userTimeFormat === '12h');

    const preset = options?.format || 'short';
    const formatOptions: Intl.DateTimeFormatOptions = {
      ...TIME_FORMAT_PRESETS[preset],
      hour12,
    };
    if (timezone) formatOptions.timeZone = timezone;

    try {
      return new Intl.DateTimeFormat(locale, formatOptions).format(date);
    } catch (error) {
      devWarn(`Time formatting failed: ${error instanceof Error ? error.message : String(error)}`);
      return date.toLocaleTimeString();
    }
  }

  function dt(
    value: string | number | Date | null | undefined,
    dateOptions?: DateFormatOptions,
    timeOptions?: TimeFormatOptions,
  ): string {
    const date = parseDate(value);
    if (!date) return '';
    return `${d(date, dateOptions)} ${time(date, timeOptions)}`;
  }

  function n(
    value: number | null | undefined,
    options?: NumberFormatOptions,
  ): string {
    if (value === null || value === undefined || isNaN(value)) return '';

    const settings = getUserSettings();
    const locale = options?.locale || buildLocale(language.value, settings?.country || null);

    const formatOptions: Intl.NumberFormatOptions = {
      style: options?.style || 'decimal',
      useGrouping: options?.useGrouping ?? true,
    };

    if (options?.minimumFractionDigits !== undefined) {
      formatOptions.minimumFractionDigits = options.minimumFractionDigits;
    }
    if (options?.maximumFractionDigits !== undefined) {
      formatOptions.maximumFractionDigits = options.maximumFractionDigits;
    }

    if (options?.style === 'currency') {
      formatOptions.currency = options.currency || 'EUR';
    }

    if (options?.style === 'unit' && options.unit) {
      formatOptions.unit = options.unit;
      formatOptions.unitDisplay = options.unitDisplay || 'short';
    }

    try {
      return new Intl.NumberFormat(locale, formatOptions).format(value);
    } catch (error) {
      devWarn(`Number formatting failed: ${error instanceof Error ? error.message : String(error)}`);
      return String(value);
    }
  }

  function c(
    value: number | null | undefined,
    currencyCode?: string,
    options?: Omit<NumberFormatOptions, 'style' | 'currency'>,
  ): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    const currency = currencyCode || 'EUR';
    return n(value, { ...options, style: 'currency', currency });
  }

  function rt(
    value: string | number | Date | null | undefined,
    options?: { locale?: string; style?: 'long' | 'short' | 'narrow' },
  ): string {
    const date = parseDate(value);
    if (!date) return '';

    const settings = getUserSettings();
    const locale = options?.locale || buildLocale(language.value, settings?.country || null);

    const now = Date.now();
    const diff = date.getTime() - now;
    const absDiff = Math.abs(diff);

    let unit: RelativeTimeUnit = 'second';
    let amount = Math.round(diff / 1000);

    for (const interval of TIME_INTERVALS) {
      if (absDiff >= interval.ms) {
        unit = interval.unit;
        amount = Math.round(diff / interval.ms);
        break;
      }
    }

    try {
      return new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
        style: options?.style || 'long',
      }).format(amount, unit);
    } catch (error) {
      devWarn(`Relative time formatting failed: ${error instanceof Error ? error.message : String(error)}`);
      const absAmount = Math.abs(amount);
      const unitStr = absAmount === 1 ? unit : `${unit}s`;
      return diff < 0 ? `${absAmount} ${unitStr} ago` : `in ${absAmount} ${unitStr}`;
    }
  }

  function dateRange(
    start: string | number | Date | null | undefined,
    end: string | number | Date | null | undefined,
    options?: DateFormatOptions,
  ): string {
    const startDate = parseDate(start);
    const endDate = parseDate(end);

    if (!startDate || !endDate) {
      if (startDate) return d(startDate, options);
      if (endDate) return d(endDate, options);
      return '';
    }

    const settings = getUserSettings();
    const locale = options?.locale || buildLocale(language.value, settings?.country || null);
    const timezone = options?.timezone || settings?.timezone || undefined;

    let formatOptions: Intl.DateTimeFormatOptions;
    if (options?.format) {
      formatOptions = typeof options.format === 'string'
        ? { ...DATE_FORMAT_PRESETS[options.format] }
        : { ...options.format };
    } else {
      const userDateFormat = settings?.dateFormat || 'Y-m-d';
      formatOptions = getDateOptionsFromPhpFormat(userDateFormat);
    }
    if (timezone) formatOptions.timeZone = timezone;

    try {
      const formatter = new Intl.DateTimeFormat(locale, formatOptions);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formatterAny = formatter as any;
      if (typeof formatterAny.formatRange === 'function') {
        return formatterAny.formatRange(startDate, endDate) as string;
      }
      return `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
    } catch (error) {
      devWarn(`Date range formatting failed: ${error instanceof Error ? error.message : String(error)}`);
      return `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`;
    }
  }

  return {
    // State (raw refs)
    currentLanguage,
    isLoading,
    loadError,

    // Getters
    language,
    userPickedLanguage,
    availableLanguages: availableLanguagesMap,
    availableLanguagesList,
    currentLanguageName,

    // Actions
    setLanguage,
    isLanguageAvailable,
    t,
    preloadLanguage,

    // Format helpers (Intl-based, read country/currency/timezone/etc.
    // from userSettings — staff Intl locale = language + optional country)
    d,
    time,
    dt,
    n,
    c,
    rt,
    dateRange,
  };
}, {
  persist: {
    pick: ['currentLanguage', 'userPickedLanguage'],
  },
});

// Re-export for convenience (legacy import path)
export { availableLanguages };
